import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { CascadeRunOutput, EvoValue, EvolutionDecisionEnvelope, EvolutionRunMode } from '../../types';
import DecisionPanel from '../DecisionPanel';
import ProvenanceBadge from './ProvenanceBadge';
import { formatEvoValue } from './format';

function defaultRequest(run_mode: EvolutionRunMode) {
  return {
    signal_id: 'S-09',
    trigger_value: {
      value: run_mode === 'projected_state' ? 3.91 : 3.62,
      unit: 'USD/gallon',
      provenance: 'public_data' as const,
      as_of: '2026-05-29T00:00:00Z',
      calibration_status: 'pending_calibration' as const,
      source_ref: 'public_diesel_seed_20260529',
    },
    scenario: 'diesel_freight_rise',
    run_mode,
    projection_horizon_days: run_mode === 'projected_state' ? 30 : null,
  };
}

export default function CascadeTrace() {
  const [runMode, setRunMode] = useState<EvolutionRunMode>('projected_state');
  const run = useMutation({ mutationFn: () => api.evolutionCascadeRun(defaultRequest(runMode)) });

  useEffect(() => {
    run.mutate();
  }, [runMode]);

  const result = run.data;
  const apexImpact = useMemo(() => result?.path.find((node) => node.stage === 'Northstar')?.delta ?? null, [result]);
  const srSummary = result ? cascadeSummary(result) : 'Cascade trace is loading.';

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div className="ph">Cascade trace</div>
          <div className="h2">Signal to Northstar to action</div>
        </div>
        <div className="decision-mode">
          {(['current_state', 'projected_state'] as const).map((mode) => (
            <button key={mode} type="button" className={runMode === mode ? 'on' : ''} onClick={() => setRunMode(mode)}>
              {mode === 'current_state' ? 'current' : 'projected'}
            </button>
          ))}
        </div>
      </div>
      <p style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{srSummary}</p>

      <div className="panel" style={{ borderLeft: `3px solid ${result?.status === 'breached' ? 'var(--red)' : 'var(--olive)'}` }}>
        {run.isPending && <div className="small faint">Tracing cascade...</div>}
        {run.isError && <div className="small" style={{ color: 'var(--red)' }}>Cascade run failed - retry.</div>}
        {result && (
          <>
            <div className="row" style={{ justifyContent: 'space-between', gap: 14, marginBottom: 12, alignItems: 'flex-start' }}>
              <Hero label="Freight cost to serve" value={result.status === 'breached' ? 'Early-warning threshold breached' : 'Within band'} hot={result.status === 'breached'} />
              <Hero label="Apex impact" value={apexImpact ? formatEvoValue(apexImpact, { signed: true }) : 'n/a'} hot={result.status === 'breached'} evo={apexImpact} />
            </div>
            {result.status === 'no_breach' && (
              <div className="panel" style={{ background: 'var(--panel2)', borderLeft: '3px solid var(--olive)', marginBottom: 12 }}>
                <span className="tag olive">CLEAR</span>{' '}
                <span className="small">Propagated - within band, no rule fired.</span>
              </div>
            )}
            <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
              <div className="flow" style={{ minWidth: 880 }}>
                {result.path.map((node, index) => (
                  <div key={node.node_id} className="row" style={{ gap: 8, alignItems: 'center' }}>
                    <StageNode node={node} hot={result.status === 'breached' && node.node_id === 'freight_cost_to_serve'} />
                    {index < result.path.length - 1 && <span className="arrow">-&gt;</span>}
                  </div>
                ))}
              </div>
            </div>
            {result.status === 'breached' && (
              <div className="panel" style={{ marginTop: 12, background: 'var(--panel2)' }}>
                <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
                  <span className="tag amber">RULE ACTION</span>
                  <b>{result.recommended_action.text}</b>
                  <span className="small faint">owner {result.recommended_action.owner}</span>
                </div>
                <DecisionPanel
                  key={result.cascade_id}
                  embedded
                  owner={result.recommended_action.owner}
                  commit={(disposition, envelope) =>
                    api.simulationDisposition({
                      simulation_kind: 'sector_shock',
                      signal_id: result.signal_id,
                      disposition,
                      owner: result.recommended_action.owner,
                      recommendation: result.recommended_action.text,
                      predicted_outcome: {
                        source: 'evolution_cascade',
                        cascade_id: result.cascade_id,
                        status: result.status,
                        evolution: evolutionEnvelope(result),
                      },
                      decision: envelope,
                    })
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Hero({ label, value, hot, evo }: { label: string; value: string; hot?: boolean; evo?: EvoValue | null }) {
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div className="small faint" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <div className="mono" style={{ fontSize: 24, fontWeight: 800, color: hot ? 'var(--red)' : 'var(--olive)' }}>
        {value}
        {evo && <ProvenanceBadge value={evo} />}
      </div>
    </div>
  );
}

function StageNode({ node, hot }: { node: CascadeRunOutput['path'][number]; hot?: boolean }) {
  const value = node.delta ?? node.value_after ?? node.value_before;
  return (
    <button type="button" className={`node ${hot ? 'hot' : ''}`} style={{ minWidth: 180, textAlign: 'left' }} title={node.source_ref ?? node.node_id}>
      <div className="small faint" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{node.stage}</div>
      <div style={{ fontWeight: 700 }}>{node.label}</div>
      <div className="mono small" style={{ marginTop: 5 }}>{formatEvoValue(value, { signed: !!node.delta })}</div>
      {value && <ProvenanceBadge value={value} />}
      {node.coefficient_confidence != null && <div className="small faint" style={{ marginTop: 5 }}>confidence pending calibration</div>}
    </button>
  );
}

function cascadeSummary(result: CascadeRunOutput) {
  const parts = result.path.map((node) => `${node.label} ${formatEvoValue(node.delta ?? node.value_after ?? node.value_before)}`);
  const owner = result.status === 'breached' ? result.recommended_action.owner : 'no owner action';
  return `${parts.join('; ')}; the early-warning threshold ${result.tripwire.breached ? 'breached' : 'clear'}; owner ${owner}.`;
}

function evolutionEnvelope(result: CascadeRunOutput): EvolutionDecisionEnvelope {
  const baseline = Object.fromEntries(
    result.path.flatMap((node) => {
      const value = node.value_before ?? node.value_after;
      return value ? [[node.node_id, value]] : [];
    }),
  );
  const expected = Object.fromEntries(
    result.path.flatMap((node) => (node.delta ? [[`${node.node_id}_delta`, node.delta]] : [])),
  );
  return {
    schema_version: 'evolution.v1',
    rule_id: result.tripwire.rule_id ?? 'distribution.freight_cost_above_band',
    prediction_type: 'type_2_structural_propagation',
    prediction_id: result.cascade_id,
    engine_ref: { engine_id: 4, engine_code: 'E4' },
    baseline_snapshot: baseline,
    expected_metric_delta: expected,
    outcome_window: result.run_mode === 'projected_state' ? '30d' : 'current',
    model_version: result.config_version,
    counterfactual_proxy: 'matched_lane_or_prior_period',
    data_quality_flag: 'partial',
    recommendation_source: result.status === 'breached' ? result.recommended_action.source : 'deterministic_rule',
  };
}
